"use client";

import React, { useMemo } from "react";
import "./SocialOrbit.css";

/** Bucket selection logic (unchanged) */
export function getBucket(count: number): 0 | 1 | 2 | 3 | 4 | 5 {
  if (count <= 3) return 0;
  if (count <= 9) return 1;
  if (count <= 19) return 2;
  if (count <= 79) return 3;
  if (count <= 160) return 4;
  return 5;
}

/** Minimal "friend" shape (keep it generic) */
export type FriendItem = any;

/** Options for layout visuals (kept identical to original mapping) */
type Options = {
  /** Override bg class per bucket, if needed */
  bgByBucket?: Partial<Record<0 | 1 | 2 | 3 | 4 | 5, string>>;
  /** Max items when bucket 4/5 (default 80) */
  clusterCap?: number;
};

/** Props for reusable orbit layout */
type SocialOrbitProps = {
  /** Entire friends array (ordered by backend; do not reshuffle) */
  friends: FriendItem[];
  /** Loading flag to show overlay loader slot */
  loading?: boolean;

  /** Render center avatar (use your AvatarDisplay with exact props) */
  renderCenter: () => React.ReactNode;

  /** Render a friend cell; receives computed size ("md" | "sm") */
  renderFriend: (friend: FriendItem, size: "md" | "sm", index: number) => React.ReactNode;

  /** Optional: custom loader slot component */
  LoaderSlot?: React.ComponentType;

  /** Optional: for >160 case */
  onOpenAll?: () => void;

  /** Optional className appended to root (e.g. "bg-soft-accent net-dots") */
  className?: string;

  /** Force a specific bucket for debugging; otherwise auto by count */
  forceBucket?: 0 | 1 | 2 | 3 | 4 | 5;

  /** Visual/layout options */
  options?: Options;
};

const SocialOrbit: React.FC<SocialOrbitProps> = ({
  friends,
  loading,
  renderCenter,
  renderFriend,
  LoaderSlot,
  onOpenAll,
  className,
  forceBucket,
  options,
}) => {
  // --- config (kept same defaults) ---
  const clusterCap = options?.clusterCap ?? 80;
  const count = friends?.length ?? 0;
  const bucket = forceBucket ?? getBucket(count);

  // bg mapping identical to original behavior
  const bgClass =
    options?.bgByBucket?.[bucket] ??
    ({
      0: "bg-quiet",
      1: "bg-soft-accent",
      2: "bg-soft-glory",
      3: "bg-soft-admin",
      4: "bg-soft-highlight",
      5: "bg-soft-highlight",
    } as const)[bucket];

  // --- helpers (same slicing semantics) ---
  const take = (n: number, offset: number) => friends.slice(offset, offset + n);
  const minTake = (n: number, offset: number) =>
    friends.slice(offset, Math.min(friends.length, offset + n));

  // --- compute sections exactly like original ---
  const {
    top,
    bottom,
    left,
    right,
    topClusters,
    bottomClusters,
    leftClusters,
    rightClusters,
    friendAvatarSize,
  } = useMemo(() => {
    let _top: FriendItem[] = [];
    let _bottom: FriendItem[] = [];
    let _left: FriendItem[] = [];
    let _right: FriendItem[] = [];
    let _topC: FriendItem[][] = [];
    let _bottomC: FriendItem[][] = [];
    let _leftC: FriendItem[][] = [];
    let _rightC: FriendItem[][] = [];
    let _size: "md" | "sm" = "md";

    if (bucket === 1) {
      _left = take(2, 0);
      _right = take(2, 2);
    }

    if (bucket === 2) {
      _top = minTake(4, 0);
      _bottom = minTake(4, _top.length);
      _left = minTake(1, _top.length + _bottom.length);
      _right = minTake(1, _top.length + _bottom.length + _left.length);
    }

    if (bucket === 3) {
      _top = minTake(8, 0);
      _bottom = minTake(8, _top.length);
      _left = minTake(2, _top.length + _bottom.length);
      _right = minTake(2, _top.length + _bottom.length + _left.length);
    }

    if (bucket === 4 || bucket === 5) {
      _size = "sm";

      const need = clusterCap;
      const selected = friends.slice(0, Math.min(need, friends.length));

      // chunk into groups of 4 (2x2)
      const chunks: FriendItem[][] = [];
      for (let i = 0; i < selected.length; i += 4) {
        chunks.push(selected.slice(i, i + 4));
      }
      const takeChunks = (n: number, offset: number) => chunks.slice(offset, offset + n);

      const _topChunks = takeChunks(7, 0); // 28
      const _bottomChunks = takeChunks(7, _topChunks.length); // 28
      const _leftChunks = takeChunks(3, _topChunks.length + _bottomChunks.length); // 12
      const _rightChunks = takeChunks(3, _topChunks.length + _bottomChunks.length + _leftChunks.length); // 12

      _topC = _topChunks;
      _bottomC = _bottomChunks;
      _leftC = _leftChunks;
      _rightC = _rightChunks;
    }

    return {
      top: _top,
      bottom: _bottom,
      left: _left,
      right: _right,
      topClusters: _topC,
      bottomClusters: _bottomC,
      leftClusters: _leftC,
      rightClusters: _rightC,
      friendAvatarSize: _size,
    };
  }, [bucket, friends, clusterCap]);

  // --- render helpers (kept same DOM/class names) ---
  const renderCluster = (cluster: FriendItem[], cIdx: number) => (
    <div className="friend-cluster" key={`cluster-${cIdx}`}>
      {cluster.map((f, i) => (
        <div key={f?.id ?? i} className="friend-cell">
          {renderFriend(f, friendAvatarSize, i)}
        </div>
      ))}
    </div>
  );

  return (
    <div className={`profile-avatars ${className ?? ""} ${bgClass}`.trim()}>
      {loading && LoaderSlot ? (
        <div className="pa-loading">
          <LoaderSlot />
        </div>
      ) : null}

      {/* center avatar (exact same slot to use your AvatarDisplay) */}
      <div className="pa-center">{renderCenter()}</div>

      {/* bucket 1: 2 left + 2 right */}
      {bucket === 1 && (
        <>
          <div className="pa-side pa-left">
            {left.map((f, i) => (
              <div key={f?.id ?? i} className="friend-cell">
                {renderFriend(f, friendAvatarSize, i)}
              </div>
            ))}
          </div>
          <div className="pa-side pa-right">
            {right.map((f, i) => (
              <div key={f?.id ?? i} className="friend-cell">
                {renderFriend(f, friendAvatarSize, i)}
              </div>
            ))}
          </div>
        </>
      )}

      {/* bucket 2-3: rows + cols */}
      {(bucket === 2 || bucket === 3) && (
        <>
          <div className="pa-row pa-top">
            {top.map((f, i) => (
              <div key={f?.id ?? i} className="friend-cell">
                {renderFriend(f, friendAvatarSize, i)}
              </div>
            ))}
          </div>
          <div className="pa-row pa-bottom">
            {bottom.map((f, i) => (
              <div key={f?.id ?? i} className="friend-cell">
                {renderFriend(f, friendAvatarSize, i)}
              </div>
            ))}
          </div>
          <div className="pa-col pa-left side-hide-on-narrow">
            {left.map((f, i) => (
              <div key={f?.id ?? i} className="friend-cell">
                {renderFriend(f, friendAvatarSize, i)}
              </div>
            ))}
          </div>
          <div className="pa-col pa-right side-hide-on-narrow">
            {right.map((f, i) => (
              <div key={f?.id ?? i} className="friend-cell">
                {renderFriend(f, friendAvatarSize, i)}
              </div>
            ))}
          </div>
        </>
      )}

      {/* bucket 4-5: clusters */}
      {(bucket === 4 || bucket === 5) && (
        <>
          <div className="pa-row pa-top clusters">{topClusters.map(renderCluster)}</div>
          <div className="pa-row pa-bottom clusters">{bottomClusters.map(renderCluster)}</div>
          <div className="pa-col pa-left clusters side-hide-on-narrow">
            {leftClusters.map(renderCluster)}
          </div>
          <div className="pa-col pa-right clusters side-hide-on-narrow">
            {rightClusters.map(renderCluster)}
          </div>

          {bucket === 5 && onOpenAll && (
            <button className="pa-view-all" type="button" onClick={onOpenAll}>
              View all friends
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default SocialOrbit;
